import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { IconCopy } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import defaultImage from "../../../assets/images/default.jpg";
import { usePermission } from "../../../hooks/usePermission";
import { permissionError, warningAccept } from "../../../util/Alert";
import ImagePreviewModal from "../../extra/ImagePreviewModal";
import Pagination from "../../extra/Pagination";
import Searching from "../../extra/Searching";
import Table from "../../extra/Table";
import ToggleSwitch from "../../extra/ToggleSwitch";
import {
  acceptSellerRequest,
  getSellerRequest,
} from "../../store/sellerRequest/sellerRequest.action";

const Section = ({ title, children }) => (
  <div style={{
    border: "1px solid #eee",
    borderRadius: 8,
    padding: 10,
    marginTop: 12,
    fontSize: 12,
    background: "#fafafa"
  }}>
    <div style={{
      fontWeight: 600,
      marginBottom: 6,
      color: "#e91e63",
      fontSize: 12
    }}>
      {title}
    </div>
    <div style={{ lineHeight: 1.4 }}>
      {children}
    </div>
  </div>
);


const SellerRequest = (props) => {
  const { can } = usePermission();
  const canEdit = can("Seller", "Edit");

  const hasPermission = useSelector((state) => state.admin.admin.flag);

  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState("");

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openSellerModal, setOpenSellerModal] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { sellerRequest } = useSelector((state) => state.sellerRequest);

  useEffect(() => {
    dispatch(getSellerRequest());
  }, [dispatch]);

  useEffect(() => {
    setData(sellerRequest);
  }, [sellerRequest]);

  // pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event, 10));
    setPage(0);
  };

  const handleClick = (id) => {
    warningAccept()
      .then((isDeleted) => {
        if (!isDeleted) return;
        if (!hasPermission) return permissionError();
        dispatch(acceptSellerRequest(id));
      })
      .catch((err) => console.log(err));
  };

  // searching
  const handleFilterData = (filteredData) => {
    if (typeof filteredData === "string") {
      setSearch(filteredData);
    } else {
      setData(filteredData);
    }
  };

  const handleEdit = (id) => {

    navigate("/admin/sellerRequestEdit", {
      state: id,
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleOpenSellerModal = (seller) => {
    setSelectedSeller(seller);
    setOpenSellerModal(true);
  };

  const handleCloseSellerModal = () => {
    setOpenSellerModal(false);
    setSelectedSeller(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const formatMobile = (row) => {
    const code = row?.countryCode?.trim();
    const number = row?.mobileNumber?.toString()?.trim();
    if (!code && !number) return "-";
    if (code && number) return `${code} ${number}`;
    return code || number;
  };

  const mapData = [
    {
      Header: "No",
      width: "20px",
      Cell: ({ index }) => <span className="text-white fw-normal">{page * rowsPerPage + parseInt(index) + 1}</span>,
    },
    {
      Header: "Business Logo",
      body: "logo",
      Cell: ({ row }) => (
        <img
          src={row?.logo || defaultImage}
          height={40}
          width={40}
          alt="logo"
          style={{ borderRadius: "8px", objectFit: "cover", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            if (!row?.logo) return;
            setSelectedPreviewImage(row.logo);
            setShowImagePreview(true);
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
        />
      ),
    },
    {
      Header: "Seller",
      body: "seller",
      Cell: ({ row }) => {
        const seller = row;
        const imageUrl = row?.image || defaultImage;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img
              src={imageUrl}
              height={45}
              width={45}
              alt="seller"
              style={{ borderRadius: "10px", objectFit: "cover", cursor: "pointer" }}
              onClick={() => handleOpenSellerModal(seller)}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = defaultImage;
              }}
            />

            <span className="text-white text-capitalize">
              {[seller?.firstName, seller?.lastName]
                .filter(Boolean)
                .join(" ") || "-"}
            </span>
          </div>
        );
      },
    },
    {
      Header: "Unique Id",
      body: "uniqueId",
      Cell: ({ row }) => (
        <p className="mb-0 text-white">
          {row?.uniqueId ? row.uniqueId : "-"}
          {row?.uniqueId && (
            <IconCopy
              size={16}
              className="ms-1"
              style={{ cursor: "pointer" }}
              onClick={() => copyToClipboard(row.uniqueId)}
            />
          )}
        </p>
      ),
    },
    
    {
      Header: "Documents",
      body: "documents",
      Cell: ({ row }) => {
        const documents = [
          { url: row?.addressProof, label: "Address Proof" },
          { url: row?.govId, label: "Gov ID" },
          { url: row?.registrationCert, label: "Registration Cert" }
        ].filter(doc => doc.url);

        return (
          <>
            {documents.length > 0 ? (
              <div className="d-flex gap-2 align-items-center justify-content-evenly">
                {documents.map((doc, index) => (
                  <img
                    key={index}
                    src={doc.url}
                    style={{
                      borderRadius: "8px",
                      cursor: "pointer",
                      border: "1px solid #ddd"
                    }}
                    height={40}
                    width={40}
                    alt={doc.label}
                    crossOrigin="anonymous"
                    title={doc.label}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!doc.url) return;
                      setSelectedPreviewImage(doc.url);
                      setShowImagePreview(true);
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultImage;
                    }}
                  />
                ))}
              </div>
            ) : (
              <span className="text-white">-</span>
            )}
          </>
        );
      },
    },
    {
      Header: "Business Name",
      body: "businessName",
      Cell: ({ row }) => (
        <span className="mb-0 text-white">
          {row?.businessName || "-"}
        </span>
      ),
    },
    {
      Header: "Business Type",
      body: "businessType",
      Cell: ({ row }) => (
        <span className="mb-0 text-white">
          {row?.businessType || "-"}
        </span>
      ),
    },
    {
      Header: "Email",
      body: "email",
      Cell: ({ row }) => (
        <span className="mb-0 text-white">{row?.email || "-"}</span>
      ),
    },
    {
      Header: "Mobile",
      body: "mobileNumber",
      Cell: ({ row }) => (
        <span className="mb-0 text-white">{formatMobile(row)}</span>
      ),
    },
    {
      Header: "Country",
      body: "country",
      Cell: ({ row }) => <span className="text-white">{row?.address?.country || "-"}</span>,
    },

    ...(canEdit ? [
{
      Header: "Accept",
      body: "isAccepted",
      Cell: ({ row }) => (
        <div className="">
          <ToggleSwitch
            value={row?.isAccepted}
            onClick={() => handleClick(row._id)}
          />
        </div>
      ),
    }
    ] : []), 
    
    {
      Header: "Created Date",
      body: "createdAt",
      Cell: ({ row }) => (
        <span className="text-white">{dayjs(row.createdAt).format("DD MMM YYYY")}</span>
      ),
    },

  ];
  return (
    <>
      <div className="mainSellerTable">

        <Modal open={openSellerModal} onClose={handleCloseSellerModal}>
          <Box sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "60%",
            maxWidth: "650px",
            bgcolor: "#fff",
            borderRadius: "14px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            p: 2,
            maxHeight: "85vh",
            overflowY: "auto"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <h3 style={{ fontSize: 18 }}>Seller Details</h3>

              <div
                onClick={handleCloseSellerModal}
                style={{
                  cursor: "pointer",
                  border: "1.5px solid #f06292",
                  borderRadius: "50%",
                  height: 24,
                  width: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#f06292",
                  fontSize: 13,
                  fontWeight: "bold"
                }}
              >✕</div>
            </div>

            {selectedSeller && (
              <>
                {/* PROFILE */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  border: "1px solid #eee",
                  padding: 12,
                  borderRadius: 10,
                  marginTop: 15
                }}>
                  <img
                    src={selectedSeller.image || defaultImage}
                    height={55}
                    width={55}
                    alt="seller"
                    style={{ borderRadius: "50%", objectFit: "cover" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultImage;
                    }}
                  />

                  <div>
                    <h4 style={{ margin: 0, fontSize: 15 }}>
                      {[selectedSeller.firstName, selectedSeller.lastName]
                        .filter(Boolean)
                        .join(" ") || "-"}
                    </h4>
                    <span style={{
                      background: "#e91e63",
                      color: "#fff",
                      padding: "3px 12px",
                      borderRadius: 20,
                      fontSize: 10
                    }}>
                      {selectedSeller.businessType || "Seller"}
                    </span>
                    {selectedSeller.uniqueId && (
                      <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                        Unique Id: {selectedSeller.uniqueId}
                      </div>
                    )}
                  </div>

                  {selectedSeller.logo && (
                    <img
                      src={selectedSeller.logo}
                      height={45}
                      width={45}
                      alt="logo"
                      title="Business Logo"
                      style={{
                        marginLeft: "auto",
                        borderRadius: 8,
                        objectFit: "cover",
                        border: "1px solid #eee"
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultImage;
                      }}
                    />
                  )}
                </div>

                {/* DETAILS */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginTop: 15
                }}>

                  <Section title="Personal Info">
                    Email: {selectedSeller.email || "N/A"}<br />
                    Mobile: {formatMobile(selectedSeller)}
                  </Section>

                  <Section title="Business">
                    Name: {selectedSeller.businessName || "N/A"}<br />
                    Type: {selectedSeller.businessType || "N/A"}<br />
                    Tag: {selectedSeller.businessTag || "N/A"}
                  </Section>

                  <Section title="Address">
                    City: {selectedSeller.address?.city || "N/A"}<br />
                    State: {selectedSeller.address?.state || "N/A"}<br />
                    Country: {selectedSeller.address?.country || "N/A"}
                  </Section>

                  <Section title="Account">
                    Unique Id: {selectedSeller.uniqueId || "N/A"}<br />
                    Joined: {dayjs(selectedSeller.createdAt).format("DD MMM YYYY")}
                  </Section>

                </div>
              </>
            )}
          </Box>
        </Modal>

        <div className="sellerTable">

          <div className="col-12 headname">Pending Seller </div>
          <div className="sellerMain">
            <div className="tableMain mt-2">
              <div className="sellerHeader primeHeader">
                <div className="row">
                  <div className="col-2"></div>

                  <div className="col-10 d-flex justify-content-end">
                    <Searching
                      type={"client"}
                      data={sellerRequest}
                      setData={setData}
                      onFilterData={handleFilterData}
                      serverSearching={handleFilterData}
                      placeholder="Search by seller, business name or tag"
                    />
                  </div>
                </div>
              </div>
              <Table
                data={data}
                mapData={mapData}
                PerPage={rowsPerPage}
                Page={page}
                type={"client"}
                loading={loading}
                shimmer={true}
              />
              <Pagination
                component="div"
                count={sellerRequest?.length}
                serverPage={page}
                type={"client"}
                onPageChange={handleChangePage}
                serverPerPage={rowsPerPage}
                totalData={sellerRequest?.length}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>
          <div className="sellerFooter primeFooter"></div>
        </div>
      </div>

      <ImagePreviewModal
        isOpen={showImagePreview}
        onClose={() => {
          setShowImagePreview(false);
          setSelectedPreviewImage("");
        }}
        image={selectedPreviewImage}
        fallbackImage={defaultImage}
      />

    </>
  );
};

export default connect(null, { getSellerRequest, acceptSellerRequest })(
  SellerRequest
);
